import { useEffect, useRef, useState } from 'react';
import { ChevronUp, LogOut } from 'lucide-react';
import { TutaraAvatar } from '../tutara-ui';

interface SidebarAccountMenuProps {
  user: {
    name: string;
    email: string;
    avatar?: string | null;
  };
  onLogout: () => void;
  isCollapsed: boolean;
  /** Secondary line under the name — e.g. the org name or "Platform Console". */
  accountLabel: string;
}

/**
 * The bottom-of-sidebar account section (avatar + name/label button that
 * opens a logout dropdown), including its own open/close state and
 * click-outside handling. Pure extraction from AdminSidebar/SuperAdminSidebar
 * — both carried byte-identical copies of this block except the label text,
 * which is why it's the one prop that varies here.
 */
export default function SidebarAccountMenu({ user, onLogout, isCollapsed, accountLabel }: SidebarAccountMenuProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  return (
    <div
      ref={userMenuRef}
      className={`relative border-t border-gray-200 dark:border-tutara-dark-mode-border bg-white dark:bg-tutara-dark-mode-surface flex-shrink-0 ${isCollapsed ? 'p-2' : 'p-3'}`}
    >
      {/* Account Menu Dropdown */}
      {userMenuOpen && (
        <div className={`absolute bg-white dark:bg-tutara-dark-mode-surface rounded-lg shadow-xl border border-gray-200 dark:border-tutara-dark-mode-border z-50 ${isCollapsed ? 'left-full bottom-0 ml-2 w-48' : 'bottom-full left-0 right-0 mb-2 mx-2'}`}>
          <div className="p-2">
            <button
              onClick={() => {
                setUserMenuOpen(false);
                onLogout();
              }}
              className="flex items-center space-x-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg w-full text-left"
            >
              <LogOut className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="text-sm text-tutara-charcoal dark:text-white">Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Account Button */}
      <button
        onClick={() => setUserMenuOpen(!userMenuOpen)}
        className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} p-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg transition-colors`}
        title={isCollapsed ? user.name : undefined}
      >
        <TutaraAvatar
          src={user.avatar || undefined}
          alt={user.name}
          fallback={user.name.split(' ').map(n => n[0]).join('')}
          size="sm"
        />
        {!isCollapsed && (
          <>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-tutara-charcoal dark:text-white truncate">
                {user.name}
              </p>
              <p className="text-xs text-tutara-primary dark:text-tutara-primary/80 truncate font-medium">
                {accountLabel}
              </p>
            </div>
            <ChevronUp className={`h-4 w-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>
    </div>
  );
}
